import {
  columnSortMenu,
  type DataTableColumn,
  type SortDirection,
  type SortMenu,
  type TableSort,
} from '@ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUrlState } from './useUrlState';

export interface ColumnSort {
  sort: TableSort | null;
  onSortChange: (key: string, direction: SortDirection) => void;
}

/** Reads `date` (ascending) and `-date` (descending); `null` for an empty value. */
export function parseColumnSort(raw: string): TableSort | null {
  const descending = raw.startsWith('-');
  const key = descending ? raw.slice(1) : raw;
  if (key === '') return null;
  return { key, direction: descending ? 'descending' : 'ascending' };
}

/** `date` or `-date`; the value for the URL and the API's `sort` parameter. */
export function formatColumnSort(sort: TableSort | null): string {
  if (!sort) return '';
  return sort.direction === 'descending' ? `-${sort.key}` : sort.key;
}

/**
 * Column sorting of a `DataTable` kept in the URL (`?spalte=date` ascending,
 * `?spalte=-date` descending), so back, reload and sharing keep it
 * (docs/ux-richtlinien.md §6.4). Without a value the list keeps its default
 * order. Spread `sort` and `onSortChange` into the table; `param` is the
 * value for an API that sorts on the server (paged lists, #188).
 */
export function useColumnSort(param = 'spalte'): ColumnSort & {
  reset: () => void;
  /** `''`, `key` or `-key` for the API's `sort` parameter. */
  value: string;
} {
  const [raw, setRaw] = useUrlState(param, '');
  const sort = parseColumnSort(raw);
  const onSortChange = useCallback(
    (key: string, direction: SortDirection) => setRaw(formatColumnSort({ key, direction })),
    [setRaw],
  );
  const reset = useCallback(() => setRaw(''), [setRaw]);
  return { sort, onSortChange, reset, value: formatColumnSort(sort) };
}

/**
 * Column sorting kept in the component, for lists inside a detail page
 * (e.g. the sales of a customer) where the URL belongs to the page.
 */
export function useLocalColumnSort(): ColumnSort & { reset: () => void; value: string } {
  const [sort, setSort] = useState<TableSort | null>(null);
  const onSortChange = useCallback(
    (key: string, direction: SortDirection) => setSort({ key, direction }),
    [],
  );
  const reset = useCallback(() => setSort(null), []);
  return { sort, onSortChange, reset, value: formatColumnSort(sort) };
}

/**
 * The phones' sort menu for the list toolbar (§6.4, #188), built from the
 * table's sortable columns: "Standard" plus one option per column.
 */
export function useSortMenu<T>(
  columns: readonly DataTableColumn<T>[],
  rows: readonly T[],
  columnSort: ColumnSort & { reset: () => void },
): SortMenu {
  const { t } = useTranslation();
  return columnSortMenu({
    columns,
    rows,
    sort: columnSort.sort,
    onSortChange: columnSort.onSortChange,
    onReset: columnSort.reset,
    label: t('common.sort'),
    defaultLabel: t('common.sortDefault'),
  });
}
